import { forwardRef } from 'react';
import cx from 'classnames';
import { LabelVariant } from '.';
import { useHighlightableText } from '../../hooks/useHighlightableText';
import { useTruncationTooltip } from '../../hooks/useTruncationTooltip';
import { useLanguage } from '../../locale';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { BaseHelpPopup } from '../HelpPopup/__internal__/';
import { Icon } from '../Icon';
import cn from './Label.css.json';
import './Label.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const Label = /*#__PURE__*/forwardRef((props, ref) => {
  const dt = useDesignTokens();
  const lang = useLanguage();
  const locale = lang.toLowerCase();
  const primaryLight = dt.themeVariant === 'light' && props.variant === LabelVariant.PrimaryLight;
  const children = useHighlightableText({
    text: props.children ?? '',
    className: cn.highlight,
    ...props.highlightableConfig
  });
  const setTruncationRef = useTruncationTooltip();
  const tokens = {
    ...getLabelDesignTokens(dt, {
      disabled: props.disabled,
      primaryLight,
      tokens: props.tokens,
      locale
    }),
    ...props.theme?.(dt)
  };
  const requiredSymbol = '*';
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.label, props.className, props.wrapText === false && cn.nowrap),
    style: {
      ...tokens,
      ...props.style
    },
    "data-testid": props.testId,
    ref: props.containerRef,
    children: [props.icon ? /*#__PURE__*/_jsx(Icon, {
      ...props.icon,
      className: cx(props.icon.className, cn.icon),
      disabled: props.disabled,
      tokens: () => props.variant === LabelVariant.PrimaryLight ? {
        fg: props.tokens?.().fg ?? (primaryLight ? dt.tokens['color-text-neutral-inverse'] : dt.tokens['color-label-text'])
      } : {}
    }) : null, /*#__PURE__*/_jsxs("span", {
      className: cn.textContainer,
      children: [/*#__PURE__*/_jsx("label", {
        htmlFor: props.htmlFor,
        className: cn.text,
        ref: element => {
          if (props.wrapText === false && !props.suppressTooltip) {
            setTruncationRef(element);
          }
          setRef(ref, element);
        },
        id: props.id,
        "aria-disabled": props.disabled,
        children: children
      }), props.required && /*#__PURE__*/_jsx("span", {
        className: cn.requiredMark,
        "aria-hidden": true,
        children: requiredSymbol
      }), props.helpPopup ? /*#__PURE__*/_jsx(BaseHelpPopup, {
        testId: postfixTestId(props.testId, 'helpPopup'),
        ...props.helpPopup,
        className: cx(props.helpPopup.className, cn.help),
        additionalAriaText: props.children
      }) : null]
    })]
  });
});
export const getLabelDesignTokens = (dt, opts) => {
  let labelFG = opts?.tokens?.().fg ?? (opts?.primaryLight ? dt.tokens['color-text-primary-inverse'] : dt.tokens['color-label-text']);
  labelFG = opts?.disabled ? opts?.primaryLight ? dt.tokens['color-action-text-emphasis-disabled'] : dt.tokens['color-action-text-subtle-disabled'] : labelFG;
  return {
    ...renderDesignTokens({
      fg: labelFG,
      'asterisk-fg': opts?.disabled ? labelFG : dt.tokens['color-status-text-alert'],
      'font-size': dt.tokens['font-size-body-text-m'],
      'font-weight': dt.tokens['font-style-weight-400'],
      'highlighted-bg': dt.tokens['color-highlight-text'],
      'highlighted-fg': dt.tokens['color-highlight-text-inverse'],
      'text-cursor': 'text',
      'icon-space-block-start': opts?.locale === 'zh-cn-pseudo' ? '0.25rem' : '0.125rem'
    }, 'label'),
    ...renderDesignTokens({
      'squish-xxs': dt.tokens['space-base-xxs']
    }, 'help-icon-spacing')
  };
};
//# sourceMappingURL=Label.js.map